import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import SelectField from 'shared/components/formFields/select-field';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';

const subnetMaskDefault = '255.255.255.0';

const subnetMaskToCidrMap: Record<string, number> = {
  '255.0.0.0': 8,
  '255.128.0.0': 9,
  '255.192.0.0': 10,
  '255.224.0.0': 11,
  '255.240.0.0': 12,
  '255.248.0.0': 13,
  '255.252.0.0': 14,
  '255.254.0.0': 15,
  '255.255.0.0': 16,
  '255.255.128.0': 17,
  '255.255.192.0': 18,
  '255.255.224.0': 19,
  '255.255.240.0': 20,
  '255.255.248.0': 21,
  '255.255.252.0': 22,
  '255.255.254.0': 23,
  [subnetMaskDefault]: 24,
  '255.255.255.128': 25,
  '255.255.255.192': 26,
  '255.255.255.224': 27,
  '255.255.255.240': 28,
  '255.255.255.248': 29,
  '255.255.255.252': 30,
};

const SubnetMaskField = () => {
  const cidrFieldName = 'cidr';
  const { t } = useTranslation('gateway');

  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const {
    gatewayDetailsHook: { isChangingSubnetMaskDisabled, currentSelectedGateway, viewingMode },
  } = useGatewaysContextProvider();

  const currentCidr = watch(cidrFieldName);

  const subnetMask = Object.keys(subnetMaskToCidrMap).find((key) => subnetMaskToCidrMap[key] === Number(currentCidr)) || subnetMaskDefault;

  const handleSubnetMaskChange = (subnetMask: string) => {
    setValue(cidrFieldName, subnetMaskToCidrMap[subnetMask], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Tooltip
      title={
        viewingMode === 'editing' && isChangingSubnetMaskDisabled(currentSelectedGateway)
          ? t('details.settings.configurationDisabledTooltipSubnetMask')
          : ''
      }
      followCursor
      slots={{
        transition: Zoom,
      }}
    >
      <SelectField
        inputProps={{ 'data-testid': 'typeInput' }}
        id="typeInput"
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 360,
              borderRadius: 0,
            },
          },
        }}
        error={!!errors[cidrFieldName]}
        helperText={errors[cidrFieldName]?.message?.toString()}
        variant="outlined"
        label={t('details.information.subnetMaskLabel')}
        value={subnetMask}
        disabled={viewingMode === 'editing' && isChangingSubnetMaskDisabled(currentSelectedGateway)}
        onChange={(event) => handleSubnetMaskChange(event.target.value as string)}
      >
        {Object.keys(subnetMaskToCidrMap).map((subnetMask) => (
          <MenuItem key={subnetMask} value={subnetMask}>
            {subnetMask}
          </MenuItem>
        ))}
      </SelectField>
    </Tooltip>
  );
};

export default SubnetMaskField;
